const API_BASE = '/api'

async function fetchAPI(endpoint: string, options: RequestInit = {}) {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(error.error || `HTTP ${response.status}`)
  }

  return response.json()
}

export interface SignUpData {
  email: string
  password: string
  name?: string
}

export interface SignInData {
  email: string
  password: string
}

export interface ConfirmSignUpData {
  email: string
  code: string
}

export const authAPI = {
  signUp: async (data: SignUpData) => {
    return fetchAPI('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  confirmSignUp: async (data: ConfirmSignUpData) => {
    return fetchAPI('/auth/confirm', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  signIn: async (data: SignInData) => {
    return fetchAPI('/auth/signin', {
      method: 'POST',
      body: JSON.stringify(data),
      credentials: 'include', // Include cookies
    })
  },

  signOut: async () => {
    return fetchAPI('/auth/signout', {
      method: 'POST',
      credentials: 'include',
      // Tokens read from HTTP-only cookies
    })
  },

  getMe: async () => {
    return fetchAPI('/auth/me', {
      credentials: 'include',
      // Token read from HTTP-only cookie
    })
  },

  refresh: async () => {
    return fetchAPI('/auth/refresh', {
      method: 'POST',
      credentials: 'include',
      // refreshToken read from HTTP-only cookie
    })
  },

  exchangeOAuthCode: async (code: string, redirectUri: string) => {
    return fetchAPI('/auth/oauth/token', {
      method: 'POST',
      body: JSON.stringify({ code, redirectUri }),
      credentials: 'include',
    })
  },
}

export const puzzlesAPI = {
  getAll: async () => {
    return fetchAPI('/puzzles', {
      credentials: 'include', // Auth required - send cookies
    })
  },

  getById: async (id: string) => {
    return fetchAPI(`/puzzles/${id}`, {
      credentials: 'include', // Auth required - send cookies
    })
  },
}

export interface GameState {
  userId: string
  puzzleId: string
  boardState: any[]
  moveCount: number
  solved: boolean
  updatedAt: string
}

export interface SaveGameStateData {
  puzzleId: string
  boardState: any[]
  moveCount: number
  solved: boolean
}

export interface UserPreferences {
  renderer?: 'simple' | 'flat' | 'gem' | 'circle'
  soundEnabled?: boolean
  animationSpeed?: number
}

export interface UserStatistics {
  puzzlesCompleted: number
  totalMoves: number
  totalPlayTimeSeconds: number
}

export interface UserProfile {
  userId: string
  preferences: UserPreferences
  statistics: UserStatistics
  createdAt: string
  updatedAt: string
}

export const gameStateAPI = {
  save: async (data: SaveGameStateData) => {
    return fetchAPI('/state/save', {
      method: 'POST',
      body: JSON.stringify(data),
      credentials: 'include',
      // Auth via HTTP-only cookie
    })
  },

  load: async (puzzleId: string): Promise<GameState> => {
    return fetchAPI(`/state/load/${puzzleId}`, {
      credentials: 'include',
      // Auth via HTTP-only cookie
    })
  },

  loadAll: async (): Promise<GameState[]> => {
    return fetchAPI('/state/all', {
      credentials: 'include',
      // Auth via HTTP-only cookie
    })
  },
}

export const profileAPI = {
  get: async (): Promise<UserProfile> => {
    return fetchAPI('/state/profile', {
      credentials: 'include',
      // Auth via HTTP-only cookie
    })
  },

  updatePreferences: async (preferences: Partial<UserPreferences>) => {
    return fetchAPI('/state/profile/preferences', {
      method: 'PUT',
      body: JSON.stringify(preferences),
      credentials: 'include',
      // Auth via HTTP-only cookie
    })
  },

  updateStatistics: async (statistics: Partial<UserStatistics>) => {
    return fetchAPI('/state/profile/statistics', {
      method: 'PUT',
      body: JSON.stringify(statistics),
      credentials: 'include',
      // Auth via HTTP-only cookie
    })
  },
}
